import React, { useCallback, useEffect, useState } from 'react';
import { Star, ChevronDown, ChevronUp, MessageSquareQuote, ClipboardCheck, UserRound } from 'lucide-react';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { easerGet } from '../../lib/easerApi';

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '');

function Stars({ value, size = 'h-4 w-4' }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`${size} ${n <= Math.round(value || 0) ? 'fill-[hsl(var(--brand-apricot))] text-[hsl(var(--brand-apricot))]' : 'text-muted-foreground/40'}`} />)}
    </span>
  );
}

/** Note globale de l'Easer : avis clients (poids 1) + contrôles qualité des managers (poids 2), avec le détail par source. */
export function EaserSatisfactionCard({ onUnauthorized }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await easerGet('/api/easer/satisfaction');
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else setData({ average: null, count: 0, recent: [] });
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Skeleton className="h-16 rounded-xl" />;

  const withComments = (data.recent || []).filter((r) => r.comment);
  return (
    <Card className="p-4 shadow-sm border-border" data-testid="easer-satisfaction-card">
      <div className="flex items-center gap-3">
        <span className="h-11 w-11 rounded-xl bg-[hsl(var(--brand-apricot))]/15 text-[hsl(var(--brand-apricot))] flex items-center justify-center flex-shrink-0"><Star className="h-5 w-5 fill-current" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Satisfaction client</p>
          {data.count ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-2xl font-semibold tracking-tight leading-none" data-testid="easer-satisfaction-average">{data.average.toFixed(1).replace('.', ',')}<span className="text-sm text-muted-foreground font-normal"> / 5</span></span>
              <Stars value={data.average} />
              <span className="text-sm text-muted-foreground" data-testid="easer-satisfaction-count">{data.count} évaluation{data.count > 1 ? 's' : ''}</span>
            </div>
          ) : <p className="text-sm text-muted-foreground" data-testid="easer-satisfaction-count">Pas encore d'évaluation : les avis clients et les contrôles qualité apparaîtront ici après vos prestations.</p>}
          {data.count > 0 && (data.clients?.count > 0 || data.quality?.count > 0) && (
            <p className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3" data-testid="easer-satisfaction-sources">
              <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" /> Clients {data.clients?.count ? `${Number(data.clients.average).toFixed(1).replace('.', ',')} (${data.clients.count})` : '—'}</span>
              <span className="inline-flex items-center gap-1"><ClipboardCheck className="h-3 w-3" /> Contrôles qualité {data.quality?.count ? `${Number(data.quality.average).toFixed(1).replace('.', ',')} (${data.quality.count}, ×${data.weights?.quality || 2})` : '—'}</span>
            </p>
          )}
        </div>
        {data.count > 0 && (
          <button type="button" onClick={() => setOpen((o) => !o)} className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary min-h-[44px] min-w-[44px]" aria-expanded={open} aria-label={open ? 'Masquer les avis' : 'Voir les avis'} data-testid="easer-satisfaction-toggle">
            {open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        )}
      </div>
      {open && data.count > 0 && (
        <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid="easer-satisfaction-recent-comments">
          <div className="grid grid-cols-5 gap-1 text-[11px] text-muted-foreground">
            {[5, 4, 3, 2, 1].map((s) => (
              <div key={s} className="flex items-center gap-1"><span>{s}</span><Star className="h-3 w-3" /><span className="font-medium text-foreground">{data.distribution?.[s] ?? 0}</span></div>
            ))}
          </div>
          {withComments.length === 0 && <p className="text-sm text-muted-foreground">Aucun commentaire pour le moment.</p>}
          {withComments.slice(0, 5).map((r, i) => (
            <div key={i} className="rounded-lg bg-secondary/60 px-3 py-2">
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="truncate">{r.label}{r.event_date ? ` · ${fmtDay(r.event_date)}` : ''}</span>
                <Stars value={r.stars} size="h-3 w-3" />
              </div>
              <p className="text-sm leading-relaxed mt-1 flex gap-1"><MessageSquareQuote className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" /> {r.comment}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
